import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Coins, Receipt, CheckCircle, Circle, CaretLeft, Warning, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "yappy", label: "Yappy" },
  { value: "ach", label: "ACH" },
  { value: "zelle", label: "Zelle" },
  { value: "bank", label: "Bank transfer" },
  { value: "credit", label: "Credit card (off-platform)" },
  { value: "other", label: "Other" },
];

export default function PackEdit() {
  const { athleteId, packId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | counted | personal
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ath, packs, att] = await Promise.all([
        api.get(`/users/${athleteId}`).then((r) => r.data).catch(() => null),
        api.get(`/athletes/${athleteId}/packages`).then((r) => r.data),
        api.get(`/athletes/${athleteId}/packages/${packId}/attendance`).then((r) => r.data),
      ]);
      const found = packs.find((p) => p.id === packId);
      if (!found) {
        toast.error("Pack not found");
        navigate(`/athletes/${athleteId}/billing`);
        return;
      }
      setAthlete(ath);
      setPkg(found);
      setAttendance(att);
      setForm({
        package_name: found.package_name || "",
        sessions_total: String(found.sessions_total ?? ""),
        purchased_at: (found.purchased_at || "").slice(0, 10),
        payment_status: found.payment_status || "paid",
        payment_method: found.payment_method || "cash",
        payment_reference: found.payment_reference || "",
        amount_paid_dollars: found.amount_paid_cents != null
          ? (found.amount_paid_cents / 100).toFixed(2)
          : "",
        note: found.note || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [athleteId, packId, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        package_name: form.package_name.trim() || null,
        sessions_total: form.sessions_total === "" ? null : Number(form.sessions_total),
        purchased_at: form.purchased_at || null,
        payment_status: form.payment_status,
        payment_method: form.payment_status === "paid" ? form.payment_method : null,
        payment_reference: form.payment_reference || null,
        amount_paid_cents: form.amount_paid_dollars === ""
          ? null
          : Math.round(Number(form.amount_paid_dollars) * 100),
        note: form.note || null,
      };
      const { data: updated } = await api.patch(`/athletes/${athleteId}/packages/${packId}`, body);
      setPkg(updated);
      toast.success("Pack updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }, [athleteId, packId, form, saving]);

  const updateStatus = async (att, status) => {
    try {
      const { data: updated } = await api.patch(
        `/attendance/${att.id}/status`,
        { status },
      );
      // Backend returns the raw attendance row; preserve the linked flags from
      // the existing UI state since the patch endpoint doesn't recompute them.
      setAttendance((rows) => rows.map((r) => (r.id === att.id
        ? { ...r, ...updated, linked_to_this_pack: r.linked_to_this_pack, linked_to_other_pack: r.linked_to_other_pack }
        : r)));
      const labels = { performed: "Performed", forfeited: "Forfeited", sick: "Sick", excused: "Excused" };
      toast.success(`${formatDate(att.date)}: ${labels[status]}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCredit = async (att) => {
    // If the session is currently credited to a different pack, moving it
    // to this pack refunds a credit to the old pack and consumes one here.
    // Confirm first because coaches were asking for a safety net when
    // reassigning across packs.
    if (att.linked_to_other_pack) {
      const ok = typeof window !== "undefined" && window.confirm(
        `${formatDate(att.date)} is currently credited to a different pack.\n\n`
        + "Move it to THIS pack? The other pack will get its credit back and one credit from this pack will be used instead.",
      );
      if (!ok) return;
    }
    setToggling(att.id);
    try {
      const { data: updated } = await api.post(
        `/athletes/${athleteId}/packages/${packId}/attendance/${att.id}/toggle-credit`,
      );
      setAttendance((rows) => rows.map((r) => (r.id === att.id ? updated : r)));
      setPkg((p) => ({ ...p, sessions_remaining: updated.pack_sessions_remaining }));
      toast.success(updated.linked_to_this_pack
        ? `${formatDate(att.date)} now counts against this pack`
        : `${formatDate(att.date)} marked as personal training`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/athletes/${athleteId}/packages/${packId}`);
      const unlinked = data.unlinked_attendance || 0;
      toast.success(unlinked > 0
        ? `Pack deleted · ${unlinked} session${unlinked === 1 ? "" : "s"} returned to personal training`
        : "Pack deleted");
      navigate(`/athletes/${athleteId}/billing`);
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };


  if (loading || !pkg || !form) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="font-data text-sm text-ink-3">Loading…</div>
      </div>
    );
  }

  const isCredit = pkg.payment_status === "credit";
  const consumed = (pkg.sessions_total ?? 0) - (pkg.sessions_remaining ?? 0);

  return (
    <div className="max-w-3xl mx-auto px-5 py-8" data-testid="pack-edit-page">
      <button
        onClick={() => navigate(`/athletes/${athleteId}/billing`)}
        className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink mb-4"
        data-testid="pack-edit-back"
      >
        <CaretLeft size={16} /> Back to billing
      </button>

      <div className="hs-label text-[10px] mb-1">Manage pack</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight">
        {pkg.package_name || "Class pack"}
      </h1>
      {athlete && (
        <p className="mt-1 text-sm text-ink-3" data-testid="pack-edit-athlete">
          {athlete.full_name || athlete.email}
        </p>
      )}

      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="hs-card p-4">
          <div className="hs-label text-[10px]">Credits left</div>
          <div className="font-data text-2xl font-semibold mt-1" data-testid="pack-edit-remaining">
            {pkg.sessions_remaining} / {pkg.sessions_total}
          </div>
        </div>
        <div className="hs-card p-4">
          <div className="hs-label text-[10px]">Already used</div>
          <div className="font-data text-2xl font-semibold mt-1">{consumed}</div>
        </div>
        <div className="hs-card p-4">
          <div className="hs-label text-[10px]">Expires</div>
          <div className="font-data text-sm mt-1">{formatDate(pkg.expires_at)}</div>
        </div>
      </div>

      {/* Edit form */}
      <form onSubmit={save} className="mt-8 hs-card p-5 space-y-4" data-testid="pack-edit-form">
        <h2 className="font-heading text-xl font-semibold">Pack details</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="hs-label">Package name</Label>
            <Input
              value={form.package_name}
              onChange={(e) => setForm({ ...form, package_name: e.target.value })}
              className="mt-2 h-11 rounded-xl"
              data-testid="pack-edit-name"
            />
          </div>
          <div>
            <Label className="hs-label">Sessions total</Label>
            <Input
              type="number"
              min={consumed}
              value={form.sessions_total}
              onChange={(e) => setForm({ ...form, sessions_total: e.target.value })}
              className="mt-2 h-11 rounded-xl font-data"
              data-testid="pack-edit-sessions"
            />
            <p className="text-[11px] text-ink-3 mt-1">Min: {consumed} (already used)</p>
          </div>
        </div>

        <div>
          <Label className="hs-label">Effective / purchased date</Label>
          <Input
            type="date"
            value={form.purchased_at}
            onChange={(e) => setForm({ ...form, purchased_at: e.target.value })}
            className="mt-2 h-11 rounded-xl font-data"
            data-testid="pack-edit-purchased"
          />
          <p className="text-[11px] text-ink-3 mt-1">Expiry recalculates from this date.</p>
        </div>

        <div>
          <Label className="hs-label">Payment status</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, payment_status: "paid" })}
              className={`p-3 rounded-xl border-2 text-left transition-colors ${
                form.payment_status === "paid"
                  ? "border-moss bg-sage/40"
                  : "border-line hover:border-line/80"
              }`}
              data-testid="pack-edit-status-paid"
            >
              <div className="flex items-center gap-1.5">
                <Receipt size={16} className="text-moss" weight="fill" />
                <span className="font-semibold text-sm">Prepaid</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, payment_status: "credit" })}
              className={`p-3 rounded-xl border-2 text-left transition-colors ${
                form.payment_status === "credit"
                  ? "border-warning bg-warning/10"
                  : "border-line hover:border-line/80"
              }`}
              data-testid="pack-edit-status-credit"
            >
              <div className="flex items-center gap-1.5">
                <Coins size={16} className="text-warning" weight="fill" />
                <span className="font-semibold text-sm">On credit</span>
              </div>
            </button>
          </div>
        </div>

        {form.payment_status === "paid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">Payment method</Label>
              <Select
                value={form.payment_method}
                onValueChange={(v) => setForm({ ...form, payment_method: v })}
              >
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="pack-edit-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="hs-label">Reference</Label>
              <Input
                value={form.payment_reference}
                onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                className="mt-2 h-11 rounded-xl"
                placeholder="check #, txn id…"
                data-testid="pack-edit-reference"
              />
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-warning/10 border border-warning/30 p-3 flex items-start gap-2">
            <Warning size={18} weight="fill" className="text-warning shrink-0 mt-0.5" />
            <div className="text-xs text-ink-2">
              On credit — credits usable immediately. Flip to <strong>Prepaid</strong> when collected.
            </div>
          </div>
        )}

        <div>
          <Label className="hs-label">Amount {isCredit ? "owed" : "received"} ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.amount_paid_dollars}
            onChange={(e) => setForm({ ...form, amount_paid_dollars: e.target.value })}
            className="mt-2 h-11 rounded-xl font-data"
            data-testid="pack-edit-amount"
          />
        </div>

        <div>
          <Label className="hs-label">Note</Label>
          <Textarea
            rows={2}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="mt-2 rounded-xl"
            placeholder="Sliding-scale, comp from referral…"
            data-testid="pack-edit-note"
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
            data-testid="pack-edit-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      {/* Attendance audit */}
      <div className="mt-8 hs-card p-5" data-testid="pack-edit-attendance">
        <h2 className="font-heading text-xl font-semibold">Sessions in this pack</h2>
        <p className="text-sm text-ink-3 mt-1">
          Use the status dropdown to mark each session as performed, forfeited, sick, or excused —
          and the toggle on the right to apply (or refund) a credit. Both work independently, so
          you can apply a forfeited or sick session against the pack just like a performed one.
        </p>

        {attendance.length > 0 && (() => {
          // Status discipline summary — counts each disposition. "Show" is the
          // count actually performed; the others are flavors of missed.
          const by = { performed: 0, forfeited: 0, sick: 0, excused: 0, courtesy: 0 };
          for (const a of attendance) by[a.status || "performed"]++;
          const total = attendance.length;
          const showRate = Math.round((by.performed / total) * 100);
          const tiles = [
            { key: "performed", label: "Performed", color: "text-moss", bg: "bg-sage/50" },
            { key: "forfeited", label: "Forfeited", color: "text-terracotta", bg: "bg-terracotta/10" },
            { key: "sick", label: "Sick", color: "text-warning", bg: "bg-warning/10" },
            { key: "excused", label: "Excused", color: "text-warning", bg: "bg-warning/10" },
            { key: "courtesy", label: "Courtesy", color: "text-moss", bg: "bg-sage/40" },
          ];
          return (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-6 gap-2" data-testid="pack-att-summary">
              {tiles.map((t) => (
                <div key={t.key} className={`px-3 py-2.5 rounded-xl ${t.bg}`} data-testid={`pack-att-summary-${t.key}`}>
                  <div className={`font-data text-2xl font-semibold ${t.color}`}>{by[t.key]}</div>
                  <div className="hs-label text-[10px] mt-0.5">{t.label}</div>
                </div>
              ))}
              <div className="px-3 py-2.5 rounded-xl bg-muted2 col-span-2 sm:col-span-1" data-testid="pack-att-summary-show-rate">
                <div className="font-data text-2xl font-semibold text-ink">{showRate}%</div>
                <div className="hs-label text-[10px] mt-0.5">Show rate</div>
              </div>
            </div>
          );
        })()}

        {(() => {
          const counts = {
            all: attendance.length,
            counted: attendance.filter((a) => a.linked_to_this_pack).length,
            personal: attendance.filter((a) => !a.linked_to_this_pack && !a.linked_to_other_pack).length,
          };
          const visible = attendance.filter((a) => {
            if (filter === "counted") return a.linked_to_this_pack;
            if (filter === "personal") return !a.linked_to_this_pack && !a.linked_to_other_pack;
            return true;
          });
          return (
            <>
              {attendance.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="pack-att-filter">
                  {[
                    { key: "all", label: "All" },
                    { key: "counted", label: "Counted" },
                    { key: "personal", label: "Personal training" },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilter(f.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        filter === f.key
                          ? "bg-ink text-bone border-ink"
                          : "bg-bone text-ink-2 border-line hover:border-ink/30"
                      }`}
                      data-testid={`pack-att-filter-${f.key}`}
                    >
                      {f.label} <span className="opacity-70">· {counts[f.key]}</span>
                    </button>
                  ))}
                </div>
              )}

              {attendance.length === 0 ? (
                <p className="mt-4 text-sm text-ink-3" data-testid="pack-edit-attendance-empty">
                  No check-ins recorded in this pack&rsquo;s window yet.
                </p>
              ) : visible.length === 0 ? (
                <p className="mt-4 text-sm text-ink-3" data-testid="pack-edit-attendance-filtered-empty">
                  No sessions match this filter.
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-line">
                  {visible.map((a) => {
                    const linked = a.linked_to_this_pack;
                    const otherPack = a.linked_to_other_pack;
                    const isBusy = toggling === a.id;
                    const status = a.status || "performed";
                    return (
                      <li
                        key={a.id}
                        className="py-3 flex flex-wrap items-center justify-between gap-3"
                        data-testid={`pack-att-row-${a.date}`}
                      >
                        <div className="min-w-0">
                          <div className="font-data text-sm font-medium">{formatDate(a.date)}</div>
                          <div className="text-[11px] text-ink-3 mt-0.5">
                            {linked
                              ? "Credit used from this pack"
                              : otherPack
                              ? "Credit used from a different pack"
                              : "Personal training (no credit consumed)"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <select
                            value={status}
                            onChange={(e) => updateStatus(a, e.target.value)}
                            className={`h-9 rounded-xl border border-line bg-bone px-2.5 font-data text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-moss/30 ${
                              status === "performed" || status === "courtesy"
                                ? "text-moss"
                                : status === "forfeited"
                                ? "text-terracotta"
                                : "text-warning"
                            }`}
                            data-testid={`pack-att-status-${a.date}`}
                            title="Set session status"
                          >
                            <option value="performed">Performed</option>
                            <option value="forfeited">Forfeited</option>
                            <option value="sick">Sick</option>
                            <option value="excused">Excused</option>
                            <option value="courtesy">Courtesy</option>
                          </select>
                          <button
                            onClick={() => toggleCredit(a)}
                            disabled={isBusy}
                            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                              linked
                                ? "bg-sage text-moss hover:bg-sage/80"
                                : otherPack
                                ? "bg-bone border-2 border-dashed border-terracotta/60 text-terracotta hover:bg-terracotta/10"
                                : "bg-bone border-2 border-line text-ink-2 hover:border-terracotta hover:text-terracotta"
                            } ${isBusy ? "opacity-60" : ""}`}
                            data-testid={`pack-att-toggle-${a.date}`}
                            title={otherPack ? "Click to move this credit to THIS pack" : (linked ? "Click to convert to personal training" : "Click to count against this pack")}
                          >
                            {linked ? <CheckCircle size={14} weight="fill" /> : <Circle size={14} />}
                            {linked
                              ? "Counts against pack"
                              : otherPack
                              ? "Assign to this pack"
                              : "Personal training"}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          );
        })()}
      </div>

      {/* Danger zone */}
      <div className="mt-8 hs-card p-5 border-terracotta/40" data-testid="pack-edit-danger">
        <h2 className="font-heading text-xl font-semibold text-terracotta">Delete pack</h2>
        <p className="text-sm text-ink-3 mt-1">
          Removes this pack entirely. Any sessions still linked are unlinked and become personal training —
          the check-in dates are preserved for your records.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setConfirmDelete(true)}
          className="mt-3 rounded-xl border-terracotta text-terracotta hover:bg-terracotta hover:text-white"
          data-testid="pack-edit-delete"
        >
          <Trash size={16} className="mr-1.5" weight="fill" /> Delete this pack
        </Button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="pack-edit-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pack?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const linked = attendance.filter((a) => a.linked_to_this_pack).length;
                if (linked === 0) {
                  return "This pack has no sessions linked to it. Deleting is safe.";
                }
                return `${linked} session${linked === 1 ? "" : "s"} ${linked === 1 ? "is" : "are"} currently counted against this pack. ${linked === 1 ? "It" : "They"} will be unlinked and marked as personal training — the check-in date${linked === 1 ? "" : "s"} stay${linked === 1 ? "s" : ""} on record.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pack-edit-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-terracotta hover:bg-terracotta/90 text-white"
              data-testid="pack-edit-delete-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete pack"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
